// React
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Refine
import { useDocumentTitle } from '@refinedev/react-router-v6';
import { useLink } from '@refinedev/core';

// Mantine
import {
  Anchor,
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  PasswordInput,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';

import {
  IconMail,
  IconLock,
  IconEyeCheck,
  IconEyeOff,
  IconShieldCheck,
  IconFingerprint,
  IconArrowRight,
  IconCheck,
  IconLoader2,
} from '@tabler/icons-react';

//utils
import { validateEmail } from 'utils/validate';
import { getLoginCardBackground } from 'utils/styleUtils';
import { nowISO, parseDateTime } from 'utils/dateUtils';

//Auth
import { authProvider } from 'authProvider';

// Sentry
import * as Sentry from '@sentry/react';

// Components
import { QDocLogo } from 'components/common/QDocLogo';

export const Login = () => {
  useDocumentTitle('Login | QBilling');
  const computedColorScheme = useComputedColorScheme('dark');
  const backgroundImage = `url(/background-${computedColorScheme}.svg)`;
  const grayThemeColor = `gray-${computedColorScheme}`;
  const redThemeColor = `red-${computedColorScheme}`;
  const errorThemeColor = `error-red-${computedColorScheme}`;
  const Link = useLink();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = decodeURIComponent(
    location.search?.split('to=')[1] || '/'
  );

  const [visible, { toggle }] = useDisclosure(false);
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      email: localStorage.getItem('email') ?? '',
      password: '',
      organization: '',
    },
    validate: {
      email: (value: string) =>
        validateEmail(value) ? null : 'Please enter a valid email address',
      password: (value: any) => (value === '' ? 'Password is required' : null),
    },
  });

  const handleLogin = async (values: any) => {
    setSubmitting(true);
    try {
      const response = (await authProvider.login({
        ...values,
        remember,
      })) as {
        type: string;
        success: boolean;
      };
      setSubmitting(false);
      switch (response.type) {
        case 'onSuccess':
          setSuccess(true);
          navigate(redirectPath);
          break;
        //MFA
        case 'confirmSignInWithSMSCode':
          navigate('/verify', { state: { mode: 'sms' } });
          break;
        case 'confirmSignInWithTOTPCode':
          navigate('/verify', { state: { mode: 'totp' } });
          break;
        case 'continueSignInWithMFASelection':
          navigate('/verify', { state: { mode: 'selection' } });
          break;
        case 'continueSignInWithTOTPSetup':
          navigate('/verify', { state: { mode: 'totp-setup' } });
          break;
        //user logged in with temporary password
        case 'newPasswordRequired':
          navigate('/update-password');
          break;
        case 'onError':
        case 'unsupportedSignInStep':
          setError('Incorrect email or password');
          form.setErrors({
            email: 'Incorrect email or password',
            password: 'Incorrect email or password',
          });
          break;
      }
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setSubmitting(false);
    }
  };

  const VisibilityToggleIcon = ({ reveal }: { reveal: boolean }) =>
    reveal ? (
      <IconEyeOff
        color={`var(--mantine-color-${grayThemeColor}-0)`}
        style={{
          width: 'var(--psi-icon-size)',
          height: 'var(--psi-icon-size)',
        }}
      />
    ) : (
      <IconEyeCheck
        color={`var(--mantine-color-${grayThemeColor}-0)`}
        style={{
          width: 'var(--psi-icon-size)',
          height: 'var(--psi-icon-size)',
        }}
      />
    );

  return (
    <Box
      style={{
        backgroundImage: backgroundImage,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundRepeat: 'no-repeat',
        height: '100vh',
        backgroundSize: 'cover',
        backgroundColor: 'transparent',
      }}
    >
      <Flex
        direction="column"
        align="center"
        justify="center"
        style={{
          height: '100%',
        }}
      >
        <Card
          withBorder
          radius="xl"
          p="lg"
          style={{
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            background: getLoginCardBackground(computedColorScheme),
            borderColor:
              computedColorScheme === 'dark'
                ? 'var(--mantine-color-border-dark-0)'
                : 'var(--mantine-color-border-light-0)',
            boxShadow: 'var(--mantine-shadow-xl)',
            transition: 'transform 160ms ease',
          }}
        >
          <Group align="center" gap="md" mt="lg" mb="xl">
            <QDocLogo />
            <div>
              <Title order={3}>Welcome back</Title>
              <Text size="sm" c="dimmed">
                Sign in to continue to your dashboard
              </Text>
            </div>
          </Group>

          <form onSubmit={form.onSubmit((values) => handleLogin(values))}>
            <Stack gap="sm">
              {/* Email */}
              <Text size="sm" fw={500}>
                <Group gap={6}>
                  <IconMail size={16} /> Email
                </Group>
              </Text>
              <Box pos="relative">
                <TextInput
                  placeholder="you@company.com"
                  size="md"
                  radius="lg"
                  leftSection={<IconMail size={16} />}
                  rightSection={<IconShieldCheck size={16} opacity={0.6} />}
                  autoComplete="email"
                  key={form.key('email')}
                  {...form.getInputProps('email')}
                />
              </Box>

              {/* Password */}
              <Text size="sm" fw={500} mt="xs">
                <Group gap={6}>
                  <IconLock size={16} /> Password
                </Group>
              </Text>
              <Box pos="relative">
                <PasswordInput
                  placeholder="••••••••"
                  size="md"
                  radius="lg"
                  visible={visible}
                  onVisibilityChange={toggle}
                  leftSection={<IconLock size={16} />}
                  visibilityToggleIcon={VisibilityToggleIcon}
                  autoComplete="current-password"
                  key={form.key('password')}
                  {...form.getInputProps('password')}
                />
              </Box>
              <Group justify="space-between" align="center">
                <Checkbox
                  checked={remember}
                  onChange={(e) => setRemember(e.currentTarget.checked)}
                  label="Remember me"
                  c={grayThemeColor}
                />
                <Anchor
                  component={Link as any}
                  to="/forgot-password"
                  size="sm"
                  c={grayThemeColor}
                >
                  Forgot password?
                </Anchor>
              </Group>

              {error && (
                <Card
                  withBorder
                  radius="md"
                  p="sm"
                  style={{
                    background: `var(--mantine-color-${errorThemeColor}-0)`,
                    borderColor: `var(--mantine-color-${redThemeColor}-0)`,
                  }}
                >
                  <Text size="sm" c={redThemeColor}>
                    {error}
                  </Text>
                </Card>
              )}

              <Button
                type="submit"
                size="md"
                radius="lg"
                disabled={submitting || success}
                leftSection={
                  submitting ? (
                    <IconLoader2 size={18} className="spin" />
                  ) : success ? (
                    <IconCheck size={18} />
                  ) : (
                    <IconArrowRight size={18} />
                  )
                }
                c={grayThemeColor}
                style={{
                  background:
                    'linear-gradient(to right, var(--mantine-color-login-gradient-start-0), var(--mantine-color-login-gradient-end-0))',
                  boxShadow:
                    '0 4px 25px var(--mantine-color-login-gradient-shadow-0)',
                }}
              >
                {submitting ? 'Signing in…' : success ? 'Signed in' : 'Sign in'}
              </Button>

              <Divider label="Security" labelPosition="center" my="xs" />

              <Group justify="space-between" mt="xs">
                <Group gap={6}>
                  <IconFingerprint size={16} />
                  <Text size="xs" c="dimmed">
                    Enterprise-grade security
                  </Text>
                </Group>
                <Group gap="xs">
                  <Anchor
                    size="xs"
                    href="https://qdoc.ca/terms-and-conditions/"
                  >
                    Privacy
                  </Anchor>
                  <Anchor
                    size="xs"
                    href="https://qdoc.ca/terms-and-conditions/"
                  >
                    Terms
                  </Anchor>
                  <Anchor size="xs" href="https://qdoc.ca/contact-us/">
                    Help
                  </Anchor>
                </Group>
              </Group>
            </Stack>
          </form>
        </Card>

        <Text ta="center" mt="xl" size="xs" c="dimmed">
          © {parseDateTime(nowISO()).year} QDoc Inc. All rights reserved.
        </Text>
      </Flex>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Box>
  );
};
